import { DAY_STRINGS } from "@clockwise/client-commons/src/util/event-recurrence";
import { SchedulingAttendanceSummaryTag } from "@clockwise/schema";
import { intersection } from "lodash";
import { Tag } from "@clockwise/schema";
import { format } from "date-fns";
import { getServerUrl } from "@util/post-message-common.util";
import { paths } from "@clockwise/client-commons/src/constants/site";
import { DateTime } from "luxon";
import { formatDuration } from "@util/duration.util";
import { singular } from "pluralize";
import { Errorable, getValue } from "./errorable.util";

export function isDateInPast(startDate: string) {
  if (!startDate) {
    return false;
  }

  // Ex. is 20210101 < 2021021? (Jan 1st 2021 less than Feb 1st 2021)
  return parseInt(startDate) < parseInt(getQueryFormattedDate(new Date())); // Date
}

// Returns the start and end date, formatted for a query, based on the rangeIndex selected.
export function getStartAndEndDate(rangeOptionIndex: number, gcalSelectedStartDate: string) {
  let startDate = gcalSelectedStartDate,
    endDate = gcalSelectedStartDate;

  switch (rangeOptionIndex) {
    case 0: // Today
      startDate = endDate = getQueryFormattedDate(new Date());
      break;
    case 1: // Tomorrow
      let tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      startDate = endDate = getQueryFormattedDate(tomorrow);
      break;
    case 2: {
      // This week
      startDate = getQueryFormattedDate(new Date());
      let _endDate = new Date();
      _endDate.setDate(_endDate.getDate() + Math.abs(5 - _endDate.getDay()));
      endDate = getQueryFormattedDate(_endDate);
      break;
    }
    case 3: {
      // Next week
      // Get date of the upcoming Monday. If Sunday, get the following Monday.
      let _startDate = new Date();
      _startDate.setDate(_startDate.getDate() + (8 - _startDate.getDay()));
      startDate = getQueryFormattedDate(_startDate);
      // Calculate endDate. It's the following Friday which is 4 days.
      let _endDate = _startDate;
      _endDate.setDate(_endDate.getDate() + 4);
      endDate = getQueryFormattedDate(_endDate);
      break;
    }
    case 4: {
      // This week or next week.
      let _startDate = new Date();
      startDate = getQueryFormattedDate(_startDate);
      // Calculate endDate. Use how we got to start of next week, and then 4 days forward of that.
      let _endDate = _startDate;
      _endDate.setDate(_startDate.getDate() + (8 - _startDate.getDay()) + 4);
      endDate = getQueryFormattedDate(_endDate);
      break;
    }
    case 5:
      const [_startDate, _endDate] = getBeginningAndEndOfWeek(
        dateObjectFromGcalDate(gcalSelectedStartDate),
      );
      startDate = getQueryFormattedDate(_startDate);
      endDate = getQueryFormattedDate(_endDate);
      break;
    case 6:
      endDate = startDate;
      break; // Keep as selectedStartDate
  }

  return { startDate, endDate };
}

export function getBeginningAndEndOfWeek(dateInWeek: Date) {
  const _startDate = new Date(dateInWeek.valueOf());
  _startDate.setDate(_startDate.getDate() - _startDate.getDay() + 1);
  _startDate.setHours(0, 0, 0, 0);
  let _endDate = new Date(_startDate.valueOf());
  _endDate.setHours(23, 59, 59, 999);
  _endDate.setDate(_endDate.getDate() + 6);

  return [_startDate, _endDate];
}

export function getFormattedDuration(_duration: number, isSpeedy: boolean) {
  let outputPrefix = isSpeedy ? "~" : "";

  if (_duration < 50) {
    if (isSpeedy) {
      if (_duration === 25) {
        return outputPrefix + "30 mins";
      }
      if (_duration === 40) {
        return outputPrefix + "45 mins";
      }
    }
    return outputPrefix + _duration + " mins";
  }

  if (isSpeedy) {
    _duration += 10;
  }

  const durationString = formatDuration(_duration, { unitDisplay: "short" })
    .replace("hr", "hrs")
    .replace("1 hrs", "1 hr")
    .replace("min", "mins");

  return outputPrefix + durationString;
}

export function getFormattedDurationSingularLong(_duration: number, isSpeedy: boolean) {
  let outputPrefix = isSpeedy ? "~" : "";

  if (_duration < 50) {
    if (isSpeedy) {
      if (_duration === 25) {
        return outputPrefix + "30 minute";
      }
      if (_duration === 40) {
        return outputPrefix + "45 minute";
      }
    }
    return outputPrefix + _duration + " minute";
  }

  if (isSpeedy) {
    _duration += 10;
  }

  const durationString = formatDuration(_duration)
    .split(" ")
    .map((word) => singular(word))
    .join(" ");

  return outputPrefix + durationString;
}

export function getFormattedDurationNonPlural(_duration: number, isSpeedy: boolean) {
  return getFormattedDuration(_duration, isSpeedy)
    .replace(" hrs", "h")
    .replace(" hr", "h")
    .replace(" mins", "m");
}

export function getQueryFormattedDateLuxon(date: DateTime) {
  return (
    "" +
    date.year +
    // luxon's months are 1-based.
    date.month.toString().padStart(2, "0") +
    date.day.toString().padStart(2, "0")
  );
}

export function getQueryFormattedDate(date: Date) {
  return (
    "" +
    date.getFullYear() +
    // JS's months are 0-based
    (date.getMonth() + 1).toString().padStart(2, "0") +
    date.getDate().toString().padStart(2, "0")
  );
}
export function getHyphenatedFormattedDateLuxon(date: DateTime) {
  return date.setLocale("en-US").toFormat("yyyy-MM-dd");
}

export function getHyphenatedFormattedDate(date: Date) {
  return format(date, "yyyy-MM-dd");
}

export function getGcalFormattedTimeLuxon(time: DateTime) {
  return formatForGcalTime(time.hour, time.minute);
}

// Format of time in Gcal modal is T+HOURS+MINUTES+00, hours and minutes left 0 padded to length 2.
export function getGcalFormattedTime(time: Date) {
  return formatForGcalTime(time.getHours(), time.getMinutes());
}

function formatForGcalTime(hour: number, minute: number) {
  return "T" + hour.toString().padStart(2, "0") + minute.toString().padStart(2, "0") + "00";
}

export function getAbbreviatedMonthAndDay(date: string) {
  return (
    getMonthFromInt(parseInt(date.substr(4, 2))).substr(0, 3) +
    " " +
    date.substr(6, 2).replace(/^0+/, "")
  );
}

export function getAbbreviatedWeek(gcalSelectedStartDate: string) {
  const [_startDate, _endDate] = getBeginningAndEndOfWeek(
    dateObjectFromGcalDate(gcalSelectedStartDate),
  );
  return (
    getAbbreviatedMonthAndDay(getQueryFormattedDate(_startDate)) +
    " - " +
    getAbbreviatedMonthAndDay(getQueryFormattedDate(_endDate))
  );
}

export function dateObjectFromGcalDate(date: string) {
  const _startDate = new Date();
  _startDate.setFullYear(parseInt(date.substr(0, 4)));
  // IMPORTANT quirk 1 - Date string month integer is the actual date, April is 4th month, but JSDate stores
  // the month zero indexed, so subtracting 1 from the parsed date month.

  // IMPORTANT quirk 2 - Must set month and date together. In case the month that you want to set only has 30 days
  // while the new Date() creates a date object that has 31 days. In this case, setting a new month that
  // doesn't have 31 days leads to JS just making the month one month later than expect.
  // Ex: new Date() makes August 31st -> set month to September -> September only has 30 days, so setMonth
  // ACTUALLY sets the month to October. Odd, but makes sense idk what else it would do.
  //
  // OR, if the month that you want to set has 31 days but the new Date() creates a date object that has 30 days,
  // much the same way, setDate sets the date to the 1st of the next month
  // So, using setMonth(monthValue, dayValue), with these comments here as a warning to others.
  _startDate.setMonth(parseInt(date.substr(4, 2)) - 1, parseInt(date.substr(6, 2)));

  return _startDate;
}

// Returns duration defined by startTime and endTime, in minutes.
export function calculateDuration(startTime: string, endTime: string) {
  const startMinutes = getMinutesIntoDay(startTime);
  const endMinutes = getMinutesIntoDay(endTime);
  let _duration;
  if (startMinutes < endMinutes) {
    // 3:30pm to 4:30pm
    _duration = endMinutes - startMinutes;
  } else {
    // Crosses 12:00am into next day. Ex. 11:00pm to 1:00am should yield 120 minutes
    _duration = 24 * 60 - startMinutes + endMinutes;
  }

  return _duration;
}

// Used by calculateDuration to get minutes into the day of a given dataTime string,
// that is then used to get a diff of two times.
// dataTime could be 11:00 am, or just 11:00 in the case of 24 hour time format.
// 2:00 pm could be 14:00, and 12:00 pm could be just 12:00.
export function getMinutesIntoDay(dataTime: string) {
  let amPM; // Default to empty string. Will remain the case for 24 hour format
  // that does not contain an am or a pm.
  if (dataTime.includes("am")) {
    amPM = "am";
  } else if (dataTime.includes("pm")) {
    amPM = "pm";
  }
  const isPartialHour = dataTime.includes(":"); //dataTime should always
  // contain a :, leaving isPartialHour always true, but I recall Google rendering
  // a time without a : once, so keeping as a backup.
  let hourInt = Number(dataTime.split(":")[0]);
  if (amPM === "pm" && hourInt < 12) {
    hourInt = hourInt + 12;
  } else if (amPM === "am" && hourInt === 12) {
    hourInt = hourInt - 12;
  }
  const minutes = isPartialHour ? dataTime.split(":")[1].split(" ")[0] : "0";

  return hourInt * 60 + parseInt(minutes);
}

export function areSameMonthDayYear(date1: Date, date2: Date) {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

export function getMonthFromInt(month: number) {
  switch (month) {
    case 1:
      return "January";
    case 2:
      return "February";
    case 3:
      return "March";
    case 4:
      return "April";
    case 5:
      return "May";
    case 6:
      return "June";
    case 7:
      return "July";
    case 8:
      return "August";
    case 9:
      return "September";
    case 10:
      return "October";
    case 11:
      return "November";
    default:
      // to satisfy Typescript need for a return path.
      return "December";
  }
}

export const getFormattedDateLuxon = (
  date: DateTime,
  forceLong: boolean = false,
  forceVeryLong: boolean = false,
) => {
  let endOfCurrentWeek = new Date();
  endOfCurrentWeek.setDate(endOfCurrentWeek.getDate() + Math.abs(6 - endOfCurrentWeek.getDay()));

  date.setLocale("en-US");

  if (date.toMillis() <= endOfCurrentWeek.getTime() && !forceLong) {
    // This week, a relative time
    return date.weekdayShort;
  } else if (forceVeryLong) {
    return date.weekdayShort + ", " + date.toLocaleString({ month: "short", day: "numeric" });
  } else {
    return date.toLocaleString({ month: "short", day: "numeric" });
  }
};

export const getFormattedDate = (
  date: Date,
  forceLong: boolean = false,
  forceVeryLong: boolean = false,
) => {
  let endOfCurrentWeek = new Date();
  endOfCurrentWeek.setDate(endOfCurrentWeek.getDate() + Math.abs(6 - endOfCurrentWeek.getDay()));

  if (date <= endOfCurrentWeek && !forceLong) {
    // This week
    return DAY_STRINGS[date.getDay()].substring(0, 3);
  } else if (forceVeryLong) {
    return (
      DAY_STRINGS[date.getDay()].substring(0, 3) +
      ", " +
      new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(new Date(date))
    );
  } else {
    return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(
      new Date(date),
    );
  }
};

export const getFormattedTimeLuxon = (date: DateTime) => {
  return date
    .setLocale("en-US")
    .toLocaleString({ hour: "numeric", minute: "numeric" })
    .toLowerCase()
    .replace(/\s/g, "");
};

export const getFormattedTime = (date: Date) => {
  return new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "numeric" })
    .format(date)
    .toLowerCase()
    .replace(/\s/g, "");
};

export const badTags = [
  SchedulingAttendanceSummaryTag.Conflict,
  SchedulingAttendanceSummaryTag.OverLunch,
  SchedulingAttendanceSummaryTag.Inconvenient,
];

// Tells if a given time slot has any warnings, is over lunch, or has conflicts.
export const hasBadTags = (tags: SchedulingAttendanceSummaryTag[]) =>
  !!intersection(badTags, tags).length;

export const badTagsSchedulingLink = [Tag.Inconvenient];

// Tells if a given time slot has any warnings, is over lunch, or has conflicts.
export const hasBadTagsSchedulingLink = (tags: (Tag | null)[]) =>
  !!intersection(badTagsSchedulingLink, tags).length;

export const getSchedulingLinkPath = (username: string, slug: string) =>
  paths.schedulingLink.replace(":linkName", username).replace(":slug", slug);
export const getSchedulingLinkUrl = (username: string, slug: string, removeHttps = false) => {
  const url = `${getServerUrl()}${getSchedulingLinkPath(username, slug)}`;
  return removeHttps
    ? url
        .replace("http://www.", "")
        .replace("https://www.", "")
        .replace("http://", "")
        .replace("https://", "")
    : url;
};

export const getCreateSchedulingLinkUrl = () => `${getServerUrl()}${paths.createSchedulingLink}`;

export const getEditSchedulingLinkPath = (slug: string) =>
  paths.editSchedulingLink.replace(":slug", slug);
export const getEditSchedulingLinkUrl = (slug: string) => {
  return `${getServerUrl()}${getEditSchedulingLinkPath(slug)}`;
};

export const sanitizeSlug = (rawSlug: string) => rawSlug.toLowerCase().replace(/\s/g, "-");

export const validateSlug = (slug: string) => {
  let slugError: string | null = null;
  let blockChange = false;
  if (slug.length === 0) {
    slugError = "A name is needed to create a URL.";
  } else if (slug.length > 40) {
    slugError = "This name is too long, the max length is 40 characters.";
    blockChange = true;
  } else if (slug.match(/[^a-zA-Z0-9-_]/)) {
    slugError = "Link URLs can only contain letters, numbers, dashes, and underscores.";
    blockChange = true;
  } else {
    slugError = "";
  }

  return { slugError, blockChange };
};

export const generateMeetingName = (
  duration: number,
  givenName?: string | null,
  familyName?: string | null,
  justDuration = false,
) => {
  const maybeName = `${givenName || ""} ${familyName || ""}`.trim();
  const postfix = `meeting ${maybeName ? "with" : ""} ${maybeName}`;
  const durationString = formatDuration(duration)
    .replace("hours", "hour")
    .replace("mins", "minute")
    .replace("minutes", "minute");

  if (justDuration) {
    return durationString;
  }

  return `${durationString} ${postfix}`.trimEnd();
};

export type CanRescheduleArgs =
  | Errorable<{
      __typename: "CanScheduleResponseList";
      list: { canReschedule: boolean }[];
    }>
  | null
  | undefined;

/**
 * For a given user and event, can an event
 * be rescheduled or made flexible
 * @param canScheduleResponse
 * @returns boolean
 */
export const canReschedule = (canScheduleResponse: CanRescheduleArgs) => {
  return getValue(canScheduleResponse)?.list[0]?.canReschedule || false;
};
